/**
 * Polyhedra Plugin
 *
 * Provides polyhedra primitives and geometry modifiers.
 * Uses the 'three' endpoint type from the Three.js plugin.
 */

import type { Plugin } from '@plugins/Plugin';
import { Domain } from '@nodes/Endpoint';
import { isBrowser } from '@runtime/env';

import { solidNode } from './nodes/SolidNode';
import { stellateNodeDef } from './nodes/StellateNode';
import { truncateNodeDef } from './nodes/TruncateNode';
import { faceColoursNodeDef } from './nodes/FaceColoursNode';

const allNodes = [
  solidNode,
  stellateNodeDef,
  truncateNodeDef,
  faceColoursNodeDef
];

// three module - loaded on register
let three: any = null;

export const polyhedraPlugin: Plugin = {
  id: 'polyhedra',
  name: 'Polyhedra',
  namespace: 'polyhedra',
  domain: Domain.Video,

  nodes: allNodes,

  async onRegister() {
    // Only load in browser
    if (!isBrowser) return;

    try {
      three = await import('three');
    } catch (err) {
      console.error('Failed to load three:', err);
    }
  },

  createNode(type: string, id: string, deps: any) {
    const nodeDef = this.nodes!.find(n => n.type === type);
    if (!nodeDef) {
      throw new Error(`Unknown Polyhedra node type: ${type}`);
    }
    // Pass three in deps
    return new nodeDef.ctor(id, { ...deps, three });
  }
};
